import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import { useSettingsStore } from '../../store/useSettingsStore';
import { useRole } from '../../hooks/useRole';
import { useMutationFeedback } from '../../hooks/useMutationFeedback';
import { getOutOfStock, isOutOfStock } from '../../utils/stockUtils';

function ToppingPicker() {
  const { t } = useTranslation();
  const role = useRole();
  const form = useSettingsStore((state) => state.form);
  const config = useAppStore((state) => state.config);
  const setForm = useSettingsStore((state) => state.setForm);
  const saveOutOfStock = useAppStore((state) => state.saveOutOfStock);
  const stockEditRequested = useAppStore((state) => state.stockEditRequested);
  const consumeStockEdit = useAppStore((state) => state.consumeStockEdit);
  const { run } = useMutationFeedback();
  // Inline out-of-stock editing (baker role): the picker itself turns into
  // the stock manager — no separate modal. Draft is topping -> out of stock.
  const [stockEditMode, setStockEditMode] = useState(false);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (stockEditRequested) {
      consumeStockEdit();
      setStockEditMode(true);
    }
  }, [stockEditRequested, consumeStockEdit]);

  useEffect(() => () => consumeStockEdit(), [consumeStockEdit]);

  const selectedToppings = Array.isArray(form.toppings) ? form.toppings : [];
  const maxToppings = parseInt(config?.maxToppings || '15', 10);
  const atLimit = selectedToppings.length >= maxToppings;
  const locked = role === 'anonymous';
  // Out-of-stock toppings are hidden entirely (both modes); the customer
  // resolves them via the verify-order notice on their order card.
  const canEditStock = role === 'baker';

  const availableToppings = useMemo(() => {
    if (!config?.categories) return [];
    return Object.entries(config.categories)
      .map(([category, items]) => ({
        category,
        items: items.map((item) => ({ category, name: item })),
      }))
      .filter((group) => group.items.length > 0);
  }, [config]);

  const toggleTopping = (name) => {
    setForm((current) => {
      const has = current.toppings.includes(name);
      if (!has && current.toppings.length >= maxToppings) {
        return current;
      }
      return {
        ...current,
        toppings: has ? current.toppings.filter((t) => t !== name) : [...current.toppings, name],
      };
    });
  };

  const enterStockEdit = () => {
    setDraft(Object.fromEntries(getOutOfStock(config).map((topping) => [topping, true])));
    setStockEditMode(true);
  };

  const cancelStockEdit = () => {
    setDraft({});
    setStockEditMode(false);
  };

  const handleSaveStock = async () => {
    const toppings = Object.entries(draft).filter(([, out]) => out).map(([topping]) => topping);
    await run(() => saveOutOfStock(toppings), t('stock.saved'));
    setDraft({});
    setStockEditMode(false);
  };

  const toggleStock = (topping) => {
    setDraft((current) => ({ ...current, [topping]: !current[topping] }));
  };

  return (
    <div className="rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-surface)] p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="text-sm font-semibold">
          {stockEditMode ? t('stock.title') : t('toppings.title')}
        </p>
        <div className="flex items-center gap-3">
          {canEditStock && !stockEditMode && (
            <button
              type="button"
              role="link"
              onClick={enterStockEdit}
              className="text-xs theme-accent underline underline-offset-2 hover:opacity-80"
            >
              {t('stock.edit')}
            </button>
          )}
          <span className="text-xs theme-muted">{t('toppings.selectedCount', { count: selectedToppings.length, max: maxToppings })}</span>
        </div>
      </div>
      <div className="space-y-4">
        {availableToppings.map(({ category, items }) => (
          <div key={category} className="space-y-1">
            <p className="pb-1 text-xs font-semibold uppercase tracking-wide theme-muted">{category}</p>
            {items.map((topping) => {
              if (stockEditMode) {
                // Stock editor: every topping visible, out-of-stock toggles.
                const out = !!draft[topping.name];
                return (
                  <label
                    key={topping.name}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition ${
                      out
                        ? 'border-red-500/40 bg-red-500/8'
                        : 'border-[color:var(--app-border)] bg-[color:var(--app-card)] hover:border-emerald-500/40'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={out}
                      onChange={() => toggleStock(topping.name)}
                      className="h-4 w-4 accent-red-400"
                    />
                    <span className={`flex-1 ${out ? 'line-through opacity-60' : ''}`}>{topping.name}</span>
                    <span className="text-xs theme-muted">{t(out ? 'stock.outOfStock' : 'stock.available')}</span>
                  </label>
                );
              }
              if (isOutOfStock(config, topping.name)) {
                return null;
              }
              const isSelected = selectedToppings.includes(topping.name);
              const disabled = locked || (!isSelected && atLimit);
              return (
                <label
                  key={topping.name}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition ${
                    isSelected
                      ? 'border-emerald-500/50 bg-emerald-500/10 theme-accent'
                      : 'border-[color:var(--app-border)] bg-[color:var(--app-card)] hover:border-emerald-500/40'
                  } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => toggleTopping(topping.name)}
                    className="h-4 w-4 accent-emerald-400"
                  />
                  <span className="flex-1">{topping.name}</span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
      {!stockEditMode && atLimit && (
        <p className="mt-3 text-xs theme-muted">{t('toppings.atLimit', { max: maxToppings })}</p>
      )}
      {stockEditMode && (
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={cancelStockEdit}
            className="rounded-md border border-[color:var(--app-border)] px-3 py-2.5 text-sm font-semibold theme-muted transition hover:opacity-80"
          >
            {t('stock.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSaveStock}
            className="rounded-md bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500"
          >
            {t('stock.save')}
          </button>
        </div>
      )}
    </div>
  );
}

export default ToppingPicker;
